// src/components/ContactPopup.jsx

import React, { useEffect, useState } from "react";

import {
  X,
  Send,
  CheckCircle,
  AlertCircle,
  User,
  Mail,
  Phone,
  Building2,
  MapPin,
  Forklift,
  CalendarDays,
  CreditCard,
  Sparkles,
  Loader2,
} from "lucide-react";

import { toast } from "react-hot-toast";
import api from "../utils/axios";

const PURCHASE_TIMEFRAMES = [
  {
    label: "Within 30 Days",
    value: "within_30_days",
  },
  {
    label: "1 - 3 Months",
    value: "1_3_months",
  },
  {
    label: "3 - 6 Months",
    value: "3_6_months",
  },
  {
    label: "6+ Months",
    value: "6_plus_months",
  },
  {
    label: "Not Sure",
    value: "not_sure",
  },
];

const PURCHASE_METHODS = [
  {
    label: "Finance",
    value: "finance",
  },
  {
    label: "Outright Purchase",
    value: "outright_purchase",
  },
];

const AUSTRALIAN_STATES = [
  "Australian Capital Territory",
  "New South Wales",
  "Northern Territory",
  "Queensland",
  "South Australia",
  "Tasmania",
  "Victoria",
  "Western Australia",
];

const initialFormData = {
  firstName: "",
  lastName: "",
  businessName: "",
  phone: "",
  email: "",
  machineModel: "",
  state: "",
  purchaseTimeframe: "",
  purchaseMethod: "",
};

const ContactPopup = ({ isOpen, onClose }) => {
  const [formData, setFormData] =
    useState(initialFormData);

  const [loading, setLoading] =
    useState(false);

  const [formErrors, setFormErrors] =
    useState({});

  const [focusedField, setFocusedField] =
    useState(null);

  const [submitSuccess, setSubmitSuccess] =
    useState(false);

  // Reset popup after closing
  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(() => {
        setFormData(initialFormData);
        setFormErrors({});
        setSubmitSuccess(false);
        setLoading(false);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Prevent body scroll while popup is open
  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow =
      "hidden";

    return () => {
      document.body.style.overflow =
        previousOverflow;
    };
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.firstName.trim()) {
      errors.firstName =
        "First name is required";
    }

    if (!formData.lastName.trim()) {
      errors.lastName =
        "Last name is required";
    }

    if (!formData.phone.trim()) {
      errors.phone =
        "Phone number is required";
    }

    if (!formData.email.trim()) {
      errors.email =
        "Email address is required";
    } else if (
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
        formData.email.trim()
      )
    ) {
      errors.email =
        "Please enter a valid email address";
    }

    if (!formData.machineModel.trim()) {
      errors.machineModel =
        "Machine / model is required";
    }

    if (!formData.state) {
      errors.state =
        "State is required";
    }

    if (!formData.purchaseTimeframe) {
      errors.purchaseTimeframe =
        "Purchase timeframe is required";
    }

    if (!formData.purchaseMethod) {
      errors.purchaseMethod =
        "Purchase method is required";
    }

    setFormErrors(errors);

    return (
      Object.keys(errors).length === 0
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error(
        "Please check the highlighted fields"
      );

      return;
    }

    setLoading(true);

    try {
      const payload = {
        firstName:
          formData.firstName.trim(),

        lastName:
          formData.lastName.trim(),

        businessName:
          formData.businessName.trim(),

        phone:
          formData.phone.trim(),

        email:
          formData.email
            .trim()
            .toLowerCase(),

        machineModel:
          formData.machineModel.trim(),

        state:
          formData.state,

        purchaseTimeframe:
          formData.purchaseTimeframe,

        purchaseMethod:
          formData.purchaseMethod,
      };

      const response =
        await api.post(
          "/contact/submit",
          payload
        );

      if (response.data?.success) {
        // Meta Pixel Lead Event
        if (
          typeof window !==
            "undefined" &&
          window.fbq
        ) {
          window.fbq(
            "track",
            "Lead"
          );
        }

        // Google Analytics Lead Event
        if (
          typeof window !==
            "undefined" &&
          window.gtag
        ) {
          window.gtag(
            "event",
            "generate_lead",
            {
              event_category:
                "Machine Enquiry",

              machine_model:
                payload.machineModel,

              state:
                payload.state,

              purchase_timeframe:
                payload.purchaseTimeframe,

              purchase_method:
                payload.purchaseMethod,
            }
          );
        }

        setSubmitSuccess(true);

        toast.success(
          "Enquiry submitted successfully!"
        );
      } else {
        toast.error(
          response.data?.error ||
            "Something went wrong"
        );
      }
    } catch (error) {
      console.error(
        "Machine enquiry submission error:",
        error
      );

      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        "Unable to submit your enquiry. Please try again.";

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getInputClass = (field) => {
    const hasError =
      formErrors[field];

    const isFocused =
      focusedField === field;

    return `
      w-full
      px-3
      py-2.5
      text-sm
      rounded-lg
      border
      transition-all
      outline-none
      text-gray-900
      dark:text-white
      placeholder-gray-400
      dark:placeholder-gray-500
      ${
        hasError
          ? "border-red-500 bg-red-50 dark:bg-red-900/10 focus:ring-2 focus:ring-red-500/20"
          : isFocused
            ? "border-yellow-500 bg-white dark:bg-gray-800 ring-2 ring-yellow-500/20"
            : "border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20"
      }
    `;
  };

  const renderError = (field) => {
    if (!formErrors[field]) {
      return null;
    }

    return (
      <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
        <AlertCircle className="w-3 h-3 shrink-0" />
        {formErrors[field]}
      </p>
    );
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-3 py-4">

      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 dark:bg-black/75 backdrop-blur-sm"
        onClick={
          loading
            ? undefined
            : onClose
        }
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative w-full max-w-2xl max-h-[94vh] transform transition-all duration-300 ease-out animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-5">

        <div className="relative flex max-h-[94vh] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-gray-900">

          {/* Header */}
          <div className="relative shrink-0 bg-gradient-to-r from-yellow-500 to-amber-600 px-5 py-4">

            <div className="flex items-center justify-between gap-4">

              <div className="flex items-center gap-3">

                <div className="rounded-xl bg-white/20 p-2">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>

                <div>
                  <h2 className="text-lg font-bold text-white">
                    Inquire About a Machine
                  </h2>

                  <p className="mt-0.5 text-xs text-white/85">
                    Tell us what you're looking for and our team will get in touch.
                  </p>
                </div>

              </div>

              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="shrink-0 rounded-lg p-2 transition-colors hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-50"
                aria-label="Close"
              >
                <X className="w-5 h-5 text-white" />
              </button>

            </div>

          </div>

          {/* Success */}
          {submitSuccess ? (
            <div className="overflow-y-auto p-8 sm:p-10">

              <div className="mx-auto max-w-md text-center">

                <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-r from-green-400 to-emerald-500 shadow-lg shadow-green-500/20">
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>

                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Thanks for your enquiry!
                </h3>

                <p className="mt-3 text-sm leading-6 text-gray-600 dark:text-gray-400">
                  We've received your machine enquiry successfully. A member of the SYM Forklifts team will review your requirements and contact you shortly.
                </p>

                <div className="mt-6 rounded-xl border border-yellow-200 bg-yellow-50 p-4 text-left dark:border-yellow-900/40 dark:bg-yellow-900/10">

                  <p className="text-xs font-semibold uppercase tracking-wide text-yellow-700 dark:text-yellow-400">
                    Enquiry submitted for
                  </p>

                  <p className="mt-1 font-semibold text-gray-900 dark:text-white">
                    {formData.machineModel}
                  </p>

                  <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                    A confirmation email has also been sent to{" "}
                    <strong>
                      {formData.email}
                    </strong>
                    .
                  </p>

                </div>

                <button
                  type="button"
                  onClick={onClose}
                  className="mt-7 inline-flex items-center justify-center rounded-lg bg-gradient-to-r from-yellow-500 to-amber-600 px-6 py-2.5 text-sm font-semibold text-black transition hover:from-yellow-600 hover:to-amber-700"
                >
                  Done
                </button>

              </div>

            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="overflow-y-auto p-5 sm:p-6"
            >

              <div className="space-y-6">

                {/* Customer Details */}
                <section>

                  <div className="mb-3">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                      Your Details
                    </h3>

                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                      Fields marked with * are required.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">

                    {/* First Name */}
                    <div>
                      <label
                        htmlFor="popup-firstName"
                        className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300"
                      >
                        First Name *
                      </label>

                      <div className="relative">

                        <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />

                        <input
                          id="popup-firstName"
                          type="text"
                          name="firstName"
                          autoComplete="given-name"
                          value={formData.firstName}
                          onChange={handleChange}
                          onFocus={() =>
                            setFocusedField(
                              "firstName"
                            )
                          }
                          onBlur={() =>
                            setFocusedField(null)
                          }
                          placeholder="First name"
                          className={`${getInputClass(
                            "firstName"
                          )} pl-9`}
                        />

                      </div>

                      {renderError(
                        "firstName"
                      )}
                    </div>

                    {/* Last Name */}
                    <div>
                      <label
                        htmlFor="popup-lastName"
                        className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300"
                      >
                        Last Name *
                      </label>

                      <div className="relative">

                        <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />

                        <input
                          id="popup-lastName"
                          type="text"
                          name="lastName"
                          autoComplete="family-name"
                          value={formData.lastName}
                          onChange={handleChange}
                          onFocus={() =>
                            setFocusedField(
                              "lastName"
                            )
                          }
                          onBlur={() =>
                            setFocusedField(null)
                          }
                          placeholder="Last name"
                          className={`${getInputClass(
                            "lastName"
                          )} pl-9`}
                        />

                      </div>

                      {renderError(
                        "lastName"
                      )}
                    </div>

                    {/* Business Name */}
                    <div className="sm:col-span-2">
                      <label
                        htmlFor="popup-businessName"
                        className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300"
                      >
                        Business Name / Organization
                      </label>

                      <div className="relative">

                        <Building2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />

                        <input
                          id="popup-businessName"
                          type="text"
                          name="businessName"
                          autoComplete="organization"
                          value={formData.businessName}
                          onChange={handleChange}
                          onFocus={() =>
                            setFocusedField(
                              "businessName"
                            )
                          }
                          onBlur={() =>
                            setFocusedField(null)
                          }
                          placeholder="Business or organization"
                          className={`${getInputClass(
                            "businessName"
                          )} pl-9`}
                        />

                      </div>
                    </div>

                    {/* Phone */}
                    <div>
                      <label
                        htmlFor="popup-phone"
                        className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300"
                      >
                        Phone *
                      </label>

                      <div className="relative">

                        <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />

                        <input
                          id="popup-phone"
                          type="tel"
                          name="phone"
                          autoComplete="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          onFocus={() =>
                            setFocusedField(
                              "phone"
                            )
                          }
                          onBlur={() =>
                            setFocusedField(null)
                          }
                          placeholder="Phone number"
                          className={`${getInputClass(
                            "phone"
                          )} pl-9`}
                        />

                      </div>

                      {renderError("phone")}
                    </div>

                    {/* Email */}
                    <div>
                      <label
                        htmlFor="popup-email"
                        className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300"
                      >
                        Email *
                      </label>

                      <div className="relative">

                        <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />

                        <input
                          id="popup-email"
                          type="email"
                          name="email"
                          autoComplete="email"
                          value={formData.email}
                          onChange={handleChange}
                          onFocus={() =>
                            setFocusedField(
                              "email"
                            )
                          }
                          onBlur={() =>
                            setFocusedField(null)
                          }
                          placeholder="Email address"
                          className={`${getInputClass(
                            "email"
                          )} pl-9`}
                        />

                      </div>

                      {renderError("email")}
                    </div>

                  </div>

                </section>

                {/* Machine Requirements */}
                <section>

                  <div className="mb-3">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                      Machine Requirements
                    </h3>

                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                      Help us understand what you need.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">

                    {/* Machine */}
                    <div className="sm:col-span-2">

                      <label
                        htmlFor="popup-machineModel"
                        className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300"
                      >
                        Machine / Model of Interest *
                      </label>

                      <div className="relative">

                        <Forklift className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />

                        <input
                          id="popup-machineModel"
                          type="text"
                          name="machineModel"
                          value={formData.machineModel}
                          onChange={handleChange}
                          onFocus={() =>
                            setFocusedField(
                              "machineModel"
                            )
                          }
                          onBlur={() =>
                            setFocusedField(null)
                          }
                          placeholder="e.g. TerrainX35"
                          className={`${getInputClass(
                            "machineModel"
                          )} pl-9`}
                        />

                      </div>

                      {renderError(
                        "machineModel"
                      )}
                    </div>

                    {/* State */}
                    <div>

                      <label
                        htmlFor="popup-state"
                        className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300"
                      >
                        State *
                      </label>

                      <div className="relative">

                        <MapPin className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />

                        <select
                          id="popup-state"
                          name="state"
                          value={formData.state}
                          onChange={handleChange}
                          onFocus={() =>
                            setFocusedField(
                              "state"
                            )
                          }
                          onBlur={() =>
                            setFocusedField(null)
                          }
                          className={`${getInputClass(
                            "state"
                          )} appearance-none pl-9 pr-8`}
                        >
                          <option value="">
                            Select state
                          </option>

                          {AUSTRALIAN_STATES.map(
                            (state) => (
                              <option
                                key={state}
                                value={state}
                              >
                                {state}
                              </option>
                            )
                          )}

                        </select>

                      </div>

                      {renderError("state")}
                    </div>

                    {/* Purchase Timeframe */}
                    <div>

                      <label
                        htmlFor="popup-purchaseTimeframe"
                        className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300"
                      >
                        Purchase Timeframe *
                      </label>

                      <div className="relative">

                        <CalendarDays className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />

                        <select
                          id="popup-purchaseTimeframe"
                          name="purchaseTimeframe"
                          value={
                            formData.purchaseTimeframe
                          }
                          onChange={handleChange}
                          onFocus={() =>
                            setFocusedField(
                              "purchaseTimeframe"
                            )
                          }
                          onBlur={() =>
                            setFocusedField(null)
                          }
                          className={`${getInputClass(
                            "purchaseTimeframe"
                          )} appearance-none pl-9 pr-8`}
                        >
                          <option value="">
                            Select timeframe
                          </option>

                          {PURCHASE_TIMEFRAMES.map(
                            (item) => (
                              <option
                                key={item.value}
                                value={item.value}
                              >
                                {item.label}
                              </option>
                            )
                          )}

                        </select>

                      </div>

                      {renderError(
                        "purchaseTimeframe"
                      )}
                    </div>

                    {/* Purchase Method */}
                    <div className="sm:col-span-2">

                      <label className="mb-2 block text-xs font-medium text-gray-700 dark:text-gray-300">
                        Purchase Method *
                      </label>

                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">

                        {PURCHASE_METHODS.map(
                          (item) => {
                            const selected =
                              formData.purchaseMethod ===
                              item.value;

                            return (
                              <button
                                type="button"
                                key={item.value}
                                onClick={() => {
                                  setFormData(
                                    (prev) => ({
                                      ...prev,
                                      purchaseMethod:
                                        item.value,
                                    })
                                  );

                                  setFormErrors(
                                    (prev) => ({
                                      ...prev,
                                      purchaseMethod:
                                        "",
                                    })
                                  );
                                }}
                                className={`
                                  flex
                                  items-center
                                  gap-3
                                  rounded-lg
                                  border
                                  px-4
                                  py-3
                                  text-left
                                  transition-all
                                  ${
                                    selected
                                      ? "border-yellow-500 bg-yellow-50 ring-2 ring-yellow-500/20 dark:bg-yellow-900/10"
                                      : "border-gray-300 bg-gray-50 hover:border-yellow-400 dark:border-gray-700 dark:bg-gray-800/50"
                                  }
                                `}
                              >

                                <div
                                  className={`
                                    flex
                                    h-9
                                    w-9
                                    shrink-0
                                    items-center
                                    justify-center
                                    rounded-lg
                                    ${
                                      selected
                                        ? "bg-yellow-500 text-black"
                                        : "bg-white text-gray-500 dark:bg-gray-800 dark:text-gray-400"
                                    }
                                  `}
                                >
                                  <CreditCard className="w-4 h-4" />
                                </div>

                                <span
                                  className={`text-sm font-medium ${
                                    selected
                                      ? "text-gray-900 dark:text-white"
                                      : "text-gray-700 dark:text-gray-300"
                                  }`}
                                >
                                  {item.label}
                                </span>

                              </button>
                            );
                          }
                        )}

                      </div>

                      {renderError(
                        "purchaseMethod"
                      )}
                    </div>

                  </div>

                </section>

                {/* Submit */}
                <div className="border-t border-gray-200 pt-5 dark:border-gray-800">

                  <button
                    type="submit"
                    disabled={loading}
                    className="flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-yellow-500 to-amber-600 py-3 text-sm font-semibold text-black transition-all hover:from-yellow-600 hover:to-amber-700 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Submitting Enquiry...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Submit Enquiry
                      </>
                    )}
                  </button>

                  <p className="mx-auto mt-3 max-w-lg text-center text-[11px] leading-5 text-gray-500 dark:text-gray-400">
                    By submitting this enquiry, you agree that the SYM Forklifts team may contact you regarding your machine requirements.
                  </p>

                </div>

              </div>

            </form>
          )}

        </div>

      </div>

    </div>
  );
};

export default ContactPopup;